import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import CareProfileForm from "../components/profiles/CareProfileForm";
import InvitationCarousel from "../components/Invitations/InvitationCarousel"; // Import the new component
import { db } from "../firebase";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import {
  FaUserEdit,
  FaTrash,
  FaUserCheck,
  FaPlusCircle,
  FaEye,
  FaSignOutAlt,
  FaSearch,
  FaStar,
  FaExclamationTriangle,
  FaUser,
} from "react-icons/fa"; // Cleaned up icons and added FaSearch and FaStar
import { useNavigate } from "react-router-dom";

const ProfilesPage = () => {
  const {
    currentUser,
    ownedProfiles,
    sharedProfiles,
    activeCareProfile,
    selectActiveCareProfile,
    deleteCareProfile,
    leaveSharedProfile,
    profilesLoading,
    profilesUnavailableOffline,
    defaultProfileId,
    setDefaultCareProfile,
  } = useAuth();
  const navigate = useNavigate();

  const [showForm, setShowForm] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [searchQuery, setSearchQuery] = useState(""); // State for the search query
  const [circleSummaries, setCircleSummaries] = useState({});
  const profileLoadBlockedOffline =
    profilesUnavailableOffline &&
    ownedProfiles.length + sharedProfiles.length === 0;

  const profilesWithOwnership = useMemo(
    () => [
      ...ownedProfiles.map((profile) => ({
        ...profile,
        isOwnedProfile: true,
      })),
      ...sharedProfiles.map((profile) => ({
        ...profile,
        isOwnedProfile: false,
      })),
    ],
    [ownedProfiles, sharedProfiles],
  );

  const filteredProfiles = useMemo(() => {
    const normalizedQuery = searchQuery.toLowerCase();

    return profilesWithOwnership.filter((profile) =>
      profile.profileName?.toLowerCase().includes(normalizedQuery),
    );
  }, [profilesWithOwnership, searchQuery]);

  useEffect(() => {
    if (!currentUser || ownedProfiles.length === 0) {
      setCircleSummaries({});
      return undefined;
    }

    const ownedProfileIds = new Set(ownedProfiles.map((profile) => profile.id));
    setCircleSummaries((previousSummaries) =>
      Object.fromEntries(
        Object.entries(previousSummaries).filter(([profileId]) =>
          ownedProfileIds.has(profileId),
        ),
      ),
    );

    const updateCircleSummary = (profileId, partialSummary) => {
      setCircleSummaries((previousSummaries) => ({
        ...previousSummaries,
        [profileId]: {
          memberCount: previousSummaries[profileId]?.memberCount ?? 1,
          pendingInviteCount:
            previousSummaries[profileId]?.pendingInviteCount ?? 0,
          ...partialSummary,
        },
      }));
    };

    const unsubscribers = ownedProfiles.flatMap((profile) => {
      const membersRef = collection(
        db,
        "careProfiles",
        profile.id,
        "careTeamMembers",
      );
      const unsubscribeMembers = onSnapshot(
        query(membersRef),
        (snapshot) => {
          const ownerInMembers = snapshot.docs.some((memberDoc) => {
            const data = memberDoc.data();
            return (
              memberDoc.id === currentUser.uid ||
              data.email === currentUser.email ||
              data.role === "Owner"
            );
          });

          updateCircleSummary(profile.id, {
            memberCount: snapshot.size + (ownerInMembers ? 0 : 1),
          });
        },
        (error) => {
          console.warn("Unable to load family circle member count:", error);
          updateCircleSummary(profile.id, { memberCount: 1 });
        },
      );

      const invitationsRef = collection(db, "invitations");
      const invitationsQuery = query(
        invitationsRef,
        where("careProfileId", "==", profile.id),
        where("status", "==", "pending"),
      );
      const unsubscribeInvitations = onSnapshot(
        invitationsQuery,
        (snapshot) =>
          updateCircleSummary(profile.id, {
            pendingInviteCount: snapshot.size,
          }),
        (error) => {
          console.warn("Unable to load pending family invitations:", error);
          updateCircleSummary(profile.id, { pendingInviteCount: 0 });
        },
      );

      return [unsubscribeMembers, unsubscribeInvitations];
    });

    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
    };
  }, [currentUser, ownedProfiles]);

  const handleAddNewProfile = () => {
    setEditingProfile(null);
    setShowForm(true);
  };

  const handleEditProfile = (profile) => {
    setEditingProfile(profile);
    setShowForm(true);
  };

  const handleDeleteProfile = async (profileId, profileName) => {
    if (
      window.confirm(
        `Are you sure you want to delete the profile "${profileName || "Unnamed Profile"}"? This action cannot be undone.`,
      )
    ) {
      try {
        await deleteCareProfile(profileId);
        // Optional: add a success message if needed
        if (editingProfile && editingProfile.id === profileId) {
          setShowForm(false);
          setEditingProfile(null);
        }
      } catch (error) {
        console.error("Error deleting profile:", error);
        window.alert(`Failed to delete profile: ${error.message}`);
      }
    }
  };

  const handleLeaveProfile = async (profileId, profileName) => {
    if (
      window.confirm(
        `Are you sure you want to leave the family circle for "${profileName}"? You will lose access to this profile.`,
      )
    ) {
      try {
        await leaveSharedProfile(profileId);
      } catch (error) {
        console.error("Error leaving profile:", error);
        window.alert(`Failed to leave profile: ${error.message}`);
      }
    }
  };

  const handleSetAsActive = (profile) => {
    selectActiveCareProfile(profile);
    // Optionally, provide user feedback like a toast notification
    // window.alert(`Profile "${profile.profileName}" is now active.`);
  };

  const handleViewProfileDetails = (profile) => {
    navigate(`/profiles/view/${profile.id}`);
  };

  const handleManageCircle = (profile) => {
    selectActiveCareProfile(profile);
    navigate(`/profiles/view/${profile.id}#family-circle`);
  };

  const handleFormSubmit = () => {
    setShowForm(false);
    setEditingProfile(null);
    // Data re-fetches/updates automatically due to onSnapshot in AuthContext
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingProfile(null);
  };

  const handleSetAsDefault = async (profileId) => {
    try {
      await setDefaultCareProfile(profileId);
      // Optionally show a success message
    } catch (error) {
      console.error("Failed to set default profile:", error);
      window.alert("Failed to set default profile. Please try again.");
    }
  };

  const renderProfileList = (profiles) => {
    if (searchQuery && profiles.length === 0) {
      return (
        <div className="text-center py-10 px-6 bg-white rounded-lg shadow-md">
          <FaSearch size={48} className="mx-auto text-gray-400 mb-4" />
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">
            No Profiles Found
          </h2>
          <p className="text-gray-500">
            No profiles matched your search for "{searchQuery}".
          </p>
        </div>
      );
    }

    if (profiles.length === 0) {
      return (
        <div className="text-center py-10 px-6 bg-white rounded-lg shadow-md">
          <FaUserEdit size={48} className="mx-auto text-gray-400 mb-4" />
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">
            No Family Health Profiles Found
          </h2>
          <p className="text-gray-500 mb-6">
            To get started, create a family health profile for yourself or
            someone in your family.
          </p>
          <button
            onClick={handleAddNewProfile}
            className="flex items-center mx-auto px-6 py-3 bg-green-600 text-white font-semibold rounded-lg shadow-md hover:bg-green-700 transition duration-150 ease-in-out"
          >
            <FaPlusCircle className="mr-2" /> Create Your First Profile
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {profiles.map((profile) => {
          const isOwnedProfile = profile.isOwnedProfile;
          const circleSummary = circleSummaries[profile.id];
          const memberCount = circleSummary?.memberCount ?? 1;
          const pendingInviteCount = circleSummary?.pendingInviteCount ?? 0;

          return (
            <div
              key={`${isOwnedProfile ? "owned" : "shared"}-${profile.id}`}
              className={`bg-white p-5 rounded-lg shadow-lg transition-all duration-300 ease-in-out hover:shadow-xl ${activeCareProfile && activeCareProfile.id === profile.id ? "ring-2 ring-blue-500 border-blue-500" : "border border-gray-200"}`}
            >
              <div className="flex flex-col justify-between h-full">
                {/* Profile Info Section */}
                <div>
                  <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-2">
                    <div className="flex items-center gap-3">
                      <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100 ring-2 ring-blue-100">
                        {profile.profilePictureURL ? (
                          <img
                            src={profile.profilePictureURL}
                            alt={profile.profileName || "Family profile"}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <FaUser className="text-lg text-slate-500" />
                        )}
                      </div>
                      <h3 className="text-xl font-semibold text-gray-800">
                        {profile.profileName || "Unnamed Profile"}
                      </h3>
                    </div>
                    {defaultProfileId === profile.id && (
                      <span className="text-xs font-medium text-yellow-800 bg-yellow-100 px-2 py-0.5 rounded-full flex items-center">
                        <FaStar className="mr-1 text-yellow-500" /> Default
                      </span>
                    )}
                  </div>
                  {/* Badges Container */}
                  <div className="flex items-center flex-wrap gap-2">
                    <span
                      className={`inline-flex items-center justify-center text-center text-xs font-medium px-3 py-1 rounded-full shadow-sm ${
                        isOwnedProfile
                          ? "text-green-700 bg-green-100"
                          : "text-indigo-700 bg-indigo-100"
                      }`}
                    >
                      {isOwnedProfile ? "My Profiles" : "Shared with me"}
                    </span>
                    {!isOwnedProfile && profile.role && (
                      <span className="inline-flex items-center justify-center text-center text-xs font-medium text-gray-700 bg-gray-200 px-3 py-1 rounded-full shadow-sm">
                        {profile.role}
                      </span>
                    )}
                    {activeCareProfile &&
                      activeCareProfile.id === profile.id && (
                        <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full">
                          Currently Active
                        </span>
                      )}
                  </div>
                  <p className="mt-3 text-sm text-gray-600">
                    {isOwnedProfile ? (
                      <>
                        Family Circle: {memberCount}{" "}
                        {memberCount === 1 ? "member" : "members"}
                        {pendingInviteCount > 0 &&
                          `, ${pendingInviteCount} pending ${pendingInviteCount === 1 ? "invite" : "invites"}`}
                      </>
                    ) : (
                      <>
                        Shared through this family circle
                        {profile.role ? ` as ${profile.role}` : ""}.
                      </>
                    )}
                  </p>
                </div>

                {/* Action Buttons Section */}
                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center flex-wrap gap-2">
                  <button
                    onClick={() => handleViewProfileDetails(profile)}
                    className="flex items-center px-3 py-1.5 text-sm bg-purple-500 text-white rounded-md hover:bg-purple-600 transition-colors shadow-sm"
                  >
                    <FaEye className="mr-1.5" /> View Details
                  </button>
                  {activeCareProfile?.id !== profile.id && (
                    <button
                      onClick={() => handleSetAsActive(profile)}
                      className="flex items-center px-3 py-1.5 text-sm bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors shadow-sm"
                    >
                      <FaUserCheck className="mr-1.5" /> Set Active
                    </button>
                  )}
                  {defaultProfileId !== profile.id && (
                    <button
                      onClick={() => handleSetAsDefault(profile.id)}
                      className="flex items-center px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors shadow-sm"
                    >
                      <FaStar className="mr-1.5" /> Make Default
                    </button>
                  )}
                  {isOwnedProfile ? (
                    <>
                      <button
                        onClick={() => handleManageCircle(profile)}
                        className="flex items-center px-3 py-1.5 text-sm bg-indigo-500 text-white rounded-md hover:bg-indigo-600 transition-colors shadow-sm"
                      >
                        Manage circle
                      </button>
                      <button
                        onClick={() => handleEditProfile(profile)}
                        className="flex items-center px-3 py-1.5 text-sm bg-yellow-500 text-white rounded-md hover:bg-yellow-600 transition-colors shadow-sm"
                      >
                        <FaUserEdit className="mr-1.5" /> Edit
                      </button>
                      <button
                        onClick={() =>
                          handleDeleteProfile(profile.id, profile.profileName)
                        }
                        className="flex items-center px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors shadow-sm"
                      >
                        <FaTrash className="mr-1.5" /> Delete
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() =>
                        handleLeaveProfile(profile.id, profile.profileName)
                      }
                      className="flex items-center px-3 py-1.5 text-sm bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors shadow-sm"
                    >
                      <FaSignOutAlt className="mr-1.5" /> Leave Circle
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (profilesLoading) {
    return (
      <div className="p-6 text-center text-gray-600">Loading profiles...</div>
    );
  }

  return (
    <div className="p-4 md:p-8 bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6 gap-4">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 text-center md:text-left">
            Family Health Profiles
          </h1>
          {!showForm && !profileLoadBlockedOffline && (
            <button
              onClick={handleAddNewProfile}
              className="flex items-center justify-center w-full md:w-auto px-4 py-2 bg-green-600 text-white font-semibold rounded-lg shadow-md hover:bg-green-700 transition duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50"
            >
              <FaPlusCircle className="mr-2" /> Add New Profile
            </button>
          )}
        </div>

        <InvitationCarousel />

        {profileLoadBlockedOffline ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center shadow-md">
            <FaExclamationTriangle
              size={44}
              className="mx-auto mb-4 text-amber-500"
            />
            <h2 className="mb-2 text-2xl font-semibold text-amber-900">
              Family profiles unavailable offline
            </h2>
            <p className="mx-auto max-w-xl text-amber-900">
              We could not load your family profiles from this device while
              offline. Reconnect to the internet and MILA Care will load your
              existing profiles instead of creating a new one.
            </p>
          </div>
        ) : showForm ? (
          <div className="bg-white p-6 rounded-lg shadow-xl mb-8">
            <h2 className="text-2xl font-semibold text-gray-700 mb-6">
              {editingProfile
                ? `Edit Profile: ${editingProfile.profileName}`
                : "Create New Profile"}
            </h2>
            <CareProfileForm
              profileToEdit={editingProfile}
              onFormSubmit={handleFormSubmit}
              onCancel={handleCancelForm}
            />
          </div>
        ) : (
          <>
            {/* Search Bar */}
            <div className="mb-6 relative">
              <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                <FaSearch className="text-gray-400" />
              </span>
              <input
                type="text"
                placeholder="Search profiles by name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {renderProfileList(filteredProfiles)}
          </>
        )}
      </div>
    </div>
  );
};

export default ProfilesPage;
